<template>
  <v-row :class="'main-container mt-6 '">
    <v-col>
      <v-card
        class="mx-auto border-radius-3 pa-7 position-relative opacity-95"
        max-width="60%"
        outlined
        elevation="5"
        id="main"
      >
        <v-list-item two-line>
          <v-list-item-content class="br-title">
            <div data-cy="mainContainer-title" class="text-h4 mb-2">{{ book.title }}</div>
            <v-list-item-title class="text-subtitle-1 mt-0">{{
              author
              }}
            </v-list-item-title>
            <span v-show="false"> {{ highlighting }}</span>
          </v-list-item-content>
        </v-list-item>

        <v-card-text
          class="mt-4 pl-10 pr-10"
          :style="`font-size:${
                        bookReadConf ? bookReadConf.fontSize : ''
                    }px !important;font-family:${mainFont}!important;`"
        >
          <Highlightable
            ref="highlightable"
            @dictionary="onDictionary"
            @highlight="onHighlight"
            @note="onNote"
          >
            <div v-if="book.formatType === 'EPUB'">
              <EpubPage
                v-for="(page, index) of epubPages"
                :key="index"
                :resource="book.resourceDir"
                :page="page.href"
                :page_id="page.number"
              ></EpubPage>
            </div>
            <div v-else>
              <ChapterContent
                v-for="(chapter, index) of book.bookChapterDTOList"
                :visible="chapter.chapterNumber === currentChapter"
                :key="index"
                :resource="book.resourceDir"
                :chapter="chapter"
              ></ChapterContent>
            </div>
          </Highlightable>
        </v-card-text>

        <v-card-actions
          class="d-flex justify-center align-center"
          v-if="currentStage === 'bookReading'"
        >
          <app-footer parentComponent="mainContainer" ref="appFooter" class="d-block"/>
        </v-card-actions>
        <div
          class="cursor-crosshair position-left opacity-1 black position-absolute pre-note-container border-radius-3"
          @mousedown.prevent="getRelativeCoordinates"
          v-if="showPanel"
        ></div>
        <v-btn
          class="position-absolute rounded-circle pa-0"
          fab
          color="secondary"
          :style="{ left: `${x_axis}px`, top: `${y_axis}px` }"
          v-if="x_axis || y_axis"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn
        >
        <NotePill
          :style="{ left: `${rectangle.left}px`, top: `${rectangle.top}px`, width: `${rectangle.width}px`,height: `${0}px`  }"
          class="position-absolute"
          v-for="({ note, rectangle }, index) of notesInPage"
          :key="index"
          :number_note="index + 1"
          :data="note"
          :visible="bookReadConf.showNotes"
        />
      </v-card>
      <UtilsPanel class="position-fixed"/>
    </v-col>
    <HighlightTypeSelect
      :dialog="showHighlightModal"
      ref="highlight"
      @close-modal="closeHighlightModal"
      @save-highlight="saveHighlight"
    />
    <HighlightSaved :dialog="showHighlightSavedModal"/>
    <NoteModal
      ref="note"
      @close-modal="closeNoteModal"
      @save-note="saveNote"
      :dialog="showNoteModal"
    />
    <IdiomModal
      :dialog="showIdiomModal"
      v-if="idiomId && idiomParentId"
      :main_idiom="parentIdiom"
      :current_country="idiomId"
      :userCountryId="userCountryId"
      @close-modal="resetIdioms"
    />
  </v-row>
</template>
<script>
    /* eslint-disable no-undef */

    import { mapActions, mapState } from 'vuex'
    import readingTimeConstant from '@/constants/ReadingTimeConstant'
    import HighlightService from '@/services/highlight'
    import BookUserNotesService from '@/services/bookUserNotes'

    import Highlightable from '@/components/reading/core/Highlightable.vue'
    import NotePill from '@/components/reading/core/NotePill.vue'
    import EpubPage from './EpubPage.vue'
    import UtilsPanel from './UtilsPanel.vue'
    import HighlightTypeSelect from './modals/HighlightTypeSelect.vue'
    import HighlightSaved from './modals/HighlightSaved.vue'
    import NoteModal from './modals/NoteModal.vue'
    import IdiomModal from './modals/IdiomModal.vue'

    import Author from '../../../mixins/author'
    import ChapterContent from './ChapterContent'

    export default {
        components: {
            ChapterContent,
            EpubPage,
            Highlightable,
            UtilsPanel,
            HighlightTypeSelect,
            HighlightSaved,
            NoteModal,
            NotePill,
            IdiomModal,
        },
        computed: {
            ...mapState({
                bookReadConf: (state) => state.bookReading.bookReadConf,
                book: (state) => state.bookReading.book,
                markerWords: (state) => state.bookReading.markerWords,
                epubPages: (state) => state.bookReading.epubPages,
                currentChapter: (state) => state.bookReading.currentChapter,
                currentChapterData: (state) => state.bookReading.currentChapterData,
                currentPage: (state) => state.bookReading.currentPage,
                currentStage: (state) => state.bookReading.currentStage,
                bookHighlightData: (state) => state.bookReading.bookHighlightData,
                user: (state) => state.userData.user,
                bookUserNotes: (state) => state.bookReading.bookUserNotes,
                linguisticVariables: (state) => state.common.linguisticVariables,
                showIdiomModal: (state) => state.bookReading.showIdiomModal,
                idiomParentId: (state) => state.bookReading.idiomParentId,
                idiomId: (state) => state.userData.user.school.baseCountryId, // Same as state.bookReading.idiomId
                readingTime: (state) => state.bookReading.readingTime,
                readingTimeInterval: (state) => state.bookReading.readingTimeInterval,
            }),
            mainFont() {
                return this.$utils.fontFamilyDictionary(this.bookReadConf.fontFamily)
            },
            highlighting() {
                const tokenSplitter = "\n";
                const vm = this
                this.bookHighlightData.forEach((item) => {
                    if (item.page === vm.currentPage) {
                        const el = document.getElementById(`highlight-${item.id}`)
                        if (!el) {
                            vm.$highlight.createOnElementClass(
                                item.id,
                                item.serialized.split(tokenSplitter),
                                `#bookpage_${vm.currentPage}`,
                                vm.setHighlighType(item.ordinalHighlightType),
                            )
                        }
                    }
                })
                const page = document.querySelector(`#bookpage_${vm.currentPage}`)
                this.linguisticVariables.forEach((item) => {
                    if (page) {
                        page.innerHTML = page.innerHTML.replaceAll(
                            new RegExp(`\\b${item.word}[sS]?[^\w<]`, 'gi'),
                            (match) => {
                                return `<b class="idioms" data-is-idiom="${true}"
                                    data-idiom-id="0" data-parent-id="${item.id}"
                                    >${match}</b>`;
                            },
                        )
                    }

                })
                return `${this.bookHighlightData.length} ${this.currentPage}`
            },
            parentIdiom() {
                const vm = this
                return this.linguisticVariables.find((el) => el.id === Number(vm.idiomParentId))
            },
            userCountryId() {
                return this.user.school?.baseCountryId;
            },
            notesInPage() {
                const vm = this
                const pageHTML = document.querySelector(`#bookpage_${this.currentPage}`)
                const main = document.querySelector(`#main`)
                if(main && pageHTML)
                    return this.bookUserNotes
                            .filter(note => Number.isInteger(note.pageChildIndex) && note.page === vm.currentPage)
                            .map(note => ({
                                note,
                                rectangle: this.getLocalRectangle(vm.recreateRange(note, pageHTML.children[note.pageChildIndex]), main),
                            }))

                return []
            },
        },
        mixins: [Author],
        data() {
            return {
                selectedText: '',
                showHighlightModal: false,
                showHighlightSavedModal: false,
                showNoteModal: false,
                showPanel: false,
                x_axis: 0,
                y_axis: 0,
                startOffset: null,
                endOffset: null,
                pageChildIndex: null,
            }
        },
        beforeDestroy() {
            this.stopReadingTime()
            this.updateReadingTime(0)
        },
        methods: {
            ...mapActions('bookReading', [
                'setHighlightData',
                'showDictionary',
                'setDictionaryMode',
                'setBookUserNotes',
                'setWord',
                'resetIdioms',
                'updateReadingTime',
                'updateReadingTimeInterval',
            ]),
            onDictionary(text) {
                const clean = text.replace(' ', ',')
                const arr = clean.split(',')
                this.resetVars()
                if (arr.length > 1 && arr[1]) {
                    this.$modal.warning(this.$t('bookReading.warnDictionary'))
                    return
                }
                this.setDictionaryMode('auto')
                this.setWord(arr[0])
                this.showDictionary()
            },
            onNote(data) {
                const { offsetValues, pageChildIndex } = data
                this.startOffset = offsetValues.startOffset
                this.endOffset = offsetValues.endOffset
                this.pageChildIndex = pageChildIndex
                this.showNoteModal = true
            },
            openNoteModal() {
                this.showPanel = false
                this.showNoteModal = true
            },
            async saveNote(data) {
                this.$refs.note.btnLoaded = true
                const content = this.$refs.note.noteContent
                const {
                    x_axis, y_axis, startOffset,
                    endOffset,
                    pageChildIndex,
                } = this
                await BookUserNotesService.createNote({
                    page: this.currentPage,
                    x_axis,
                    y_axis,
                    content,
                    startOffset,
                    endOffset,
                    pageChildIndex,
                    bookUser: { id: this.user.id },
                    bookChapter: { id: this.currentChapterData.id },
                })
                    .then((response) => {
                        this.setBookUserNotes(response.content)
                    })
                    .catch((error) => console.error(error))
                this.$refs.note.resetVars()
                this.resetVars()
            },
            closeNoteModal() {
                this.showNoteModal = false
                this.showPanel = false
                this.resetVars()
            },
            onHighlight(text) {
                if (text.split(' ').length < 5) {
                    this.$modal.warning(this.$t('bookReading.warnMark'))
                    this.resetVars()
                    return
                }
                this.selectedText = text
                this.showHighlightModal = true
            },
            async saveHighlight() {
                const vm = this
                this.$refs.highlight.btnLoaded = true
                const type = this.$refs.highlight.typeSelected

                if (type === null) {
                    this.$modal.warning(this.$t('bookReading.warnMarkSelectType'))
                    this.$refs.highlight.resetVars()
                    return
                }

                await HighlightService.newHighlight({
                    userId: this.user.id,
                    chapterId: this.currentChapterData.id,
                    bookId: this.book.id,
                    page: this.currentPage,
                    quantity: this.selectedText.length,
                    serialized: this.selectedText,
                    highlightType: type,
                })
                    .then((response) => {
                        this.setHighlightData(response.content)
                    })
                    .catch((error) => console.error(error))
                this.showHighlightSavedModal = true
                setTimeout(() => {
                    vm.showHighlightSavedModal = false
                }, 5000)

                this.resetVars()
            },
            closeHighlightModal() {
                this.resetVars()
            },
            resetVars() {
                this.selectedText = ''
                this.x_axis = 0
                this.y_axis = 0
                this.showHighlightModal = false
                this.showNoteModal = false
                this.$refs.highlight.btnLoaded = false
                this.$refs.highlightable.resetVars()
                this.$refs.highlight.resetVars()
            },
            setHighlighType(type) {
                switch (type) {
                    case 0:
                        return 'characters'
                    case 1:
                        return 'actions'
                    case 2:
                        return 'vocabulary'
                    case 3:
                        return 'places'
                    default:
                        return ''
                }
            },
            getRelativeCoordinates(e) {
                const rect = e.target.getBoundingClientRect()
                const x = e.clientX - rect.left
                const y = e.clientY - rect.top
                this.x_axis = x.toFixed()
                this.y_axis = y.toFixed()
                this.openNoteModal()
            },
            viewportToHost(viewportRectangle = null, rangeContainer = null, hostContainer = null) {
                const hostRectangle = hostContainer.getBoundingClientRect()

                // Both the selection rectangle and the host rectangle are calculated relative to
                // the browser viewport. As such, the local position of the selection within the
                // host element should just be the delta of the two rectangles.
                let localLeft = (viewportRectangle.left - hostRectangle.left)
                let localTop = (viewportRectangle.top - hostRectangle.top)

                let node = rangeContainer
                // Now that we have the local position, we have to account for any scrolling
                // being performed within the host element. Let's walk from the range container
                // up to the host element and add any relevant scroll offsets to the calculated
                // local position.
                do {
                    localLeft += node.scrollLeft
                    localTop += node.scrollTop
                    // tslint:disable-next-line: no-conditional-assignment
                } while ((node !== hostContainer) && (node = node.parentNode))
                return ({
                    left: localLeft,
                    top: localTop,
                    width: viewportRectangle.width,
                    height: viewportRectangle.height,
                })
            },
            recreateRange(offsets, containerElement) {
                let charIndex = 0
                const range = new Range()
                range.setStart(containerElement, 0)
                range.collapse(true)
                const nodeStack = [containerElement]
                let node
                let foundStart = false
                let stop = false

                while (!stop && (node = nodeStack.pop())) {
                    if (node.nodeType === 3) {
                        const nextCharIndex = charIndex + node.length
                        if (!foundStart && offsets.startOffset >= charIndex && offsets.startOffset <= nextCharIndex) {
                            range.setStart(node, offsets.startOffset - charIndex)
                            foundStart = true
                        }
                        if (foundStart && offsets.endOffset >= charIndex && offsets.endOffset <= nextCharIndex) {
                            range.setEnd(node, offsets.endOffset - charIndex)
                            stop = true
                        }
                        charIndex = nextCharIndex
                    } else {
                        let i = node.childNodes.length
                        while (i--) {
                            nodeStack.push(node.childNodes[i])
                        }
                    }
                }
                return range
            },
            getLocalRectangle(range, bookContentContainer) {
                return this.viewportToHost(range.getBoundingClientRect(), this.getRangeContainer(range), bookContentContainer)
            },
            getRangeContainer(range) {
                let container = range.commonAncestorContainer
                while (container.nodeType !== 1) {
                    container = container.parentNode
                }
                return container
            },
            startReadingTime() {
                const interval = setInterval(() => {
                    this.updateReadingTime(this.readingTime + readingTimeConstant.MIN_TIME_IN_MILIS)
                }, readingTimeConstant.MIN_TIME_IN_MILIS)
                this.updateReadingTimeInterval(interval)
            },
            stopReadingTime() {
                if (this.readingTimeInterval) {
                    window.clearInterval(this.readingTimeInterval)
                }
                this.updateReadingTimeInterval(null)
            },
            isReading() {
                this.$modal.info(
                    this.$t('bookReading.inactivity_modal'),
                )
            },
            readingBookTextIsDisplayed() {
                return this.currentStage === 'bookReading'
            }
        },
        watch: {
            currentStage() {
                if (this.readingBookTextIsDisplayed()) {
                    this.startReadingTime()
                } else {
                    this.stopReadingTime()
                    this.updateReadingTime(0)
                }
            },
            readingTime() {
                if (this.readingTime === 0 && this.readingBookTextIsDisplayed()) {
                    this.stopReadingTime()
                    this.startReadingTime()
                }
                if (this.readingTime === readingTimeConstant.INACTIVITY_TIME_IN_MILIS) {
                    this.isReading()
                }
                if (this.readingTime === readingTimeConstant.MAX_TIME_IN_MILIS) {
                    this.stopReadingTime()
                }
            },
        },
    }
</script>
<style>
  .chapter_title {
    font-size: 36px;
    font-weight: bolder;
    text-align: center;
    padding-bottom: 1em;
  }

  .bookpage {
    text-align: justify;
    line-height: 1.8rem;
  }

  .br-title,
  .chapter_title,
  .bookpage,
  .footer-text {
    color: #4a3216;
  }

  .bookpage .ilustration, .bookpage .illustration {
    text-align: center;
  }

  .bookpage .ilustration > img, .bookpage .illustration > img {
    width: 95%;
  }

  .stanza_content {
      margin-top: 0;
      margin-bottom: 0 !important;
  }

  .pre-note-container {
    top: 0;
    bottom: 0;
    width: 100%;
    left: 0;
  }

  .idioms {
    border: 0px;
    border-bottom: 2px #4e342e;
    border-style: dotted;
  }

  .idioms:hover {
    cursor: pointer;
  }
</style>
